import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { ScenarioGeneratorExt } from '@/classes/scenario/genset/ScenarioGeneratorExt';
import { GensetExt } from '@/classes/scenario/genset/GensetExt';
import { GeneratorTechnology, GeneratorTypeRef } from '@/dto/scenario/generator/GeneratorReference';
import { IRule } from '@/classes/rules/IRule';
import { MinMaxRule } from '@/classes/rules/MinMaxRule';
import { Preset } from '@/services/referencesService';

interface References {
  getGeneratorRefs: () => Promise<Preset<typeof GeneratorTypeRef>>;
  getFuelRefs: () => Promise<Preset<typeof GeneratorTechnology>>;
}

interface RuleHelper {
  getFromRulesDictionary: (path: string[]) => IRule | undefined
}

interface Dependencies {
  references: References;
  ruleHelper: RuleHelper;
}

export interface UpdateTechnologyPayload {
  technology: GeneratorTechnology
  generatorNumber: 1 | 2
}

const getDefaultFields = () => {
  const defaultFields = new ScenarioGeneratorExt();
  defaultFields.generatorBeforeDay = new GensetExt();
  return defaultFields;
};

interface UnitDefinition {
  field: (generatorNumber: number) => string[]
  unit: string
}

const liquidTechnologies: Record<GeneratorTechnology, UnitDefinition[]> = ([
  GeneratorTechnology.lpg,
  GeneratorTechnology.lng,
  GeneratorTechnology.diesel,
  GeneratorTechnology.heavy_fuel_oil,
  GeneratorTechnology.kerosene
] as const)
  .map((technology: GeneratorTechnology): Record<string, UnitDefinition[]> => ({
    [technology]: [
      { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'pci'], unit: 'kwh/l' },
      { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionAt25Percent'], unit: 'l/h' },
      { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionAt50Percent'], unit: 'l/h' },
      { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionAt75Percent'], unit: 'l/h' },
      { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionAt100Percent'], unit: 'l/h' },
      { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionKWhAt25Percent'], unit: 'l/kvahour' },
      { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionKWhAt50Percent'], unit: 'l/kvahour' },
      { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionKWhAt75Percent'], unit: 'l/kvahour' },
      { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionKWhAt100Percent'], unit: 'l/kvahour' },
      { field: (generatorNumber: number) => ['scenario', 'eco', `fuelPriceType${generatorNumber}`], unit: '$/l' }
    ]
  }))
  .reduce((acc: Record<GeneratorTechnology, UnitDefinition[]>, current: Record<GeneratorTechnology, UnitDefinition[]>) => ({ ...acc, ...current }));

const unitsReferential: Record<GeneratorTechnology, UnitDefinition[]> = {
  ...liquidTechnologies,
  [GeneratorTechnology.gaz]: [
    { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'pci'], unit: 'kwh/Nm3' },
    { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionAt25Percent'], unit: 'Nm3/h' },
    { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionAt50Percent'], unit: 'Nm3/h' },
    { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionAt75Percent'], unit: 'Nm3/h' },
    { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionAt100Percent'], unit: 'Nm3/h' },
    { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionKWhAt25Percent'], unit: 'Nm3/kvahour' },
    { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionKWhAt50Percent'], unit: 'Nm3/kvahour' },
    { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionKWhAt75Percent'], unit: 'Nm3/kvahour' },
    { field: (generatorNumber: number) => ['scenario', 'generator', `generatorAfterType${generatorNumber}`, 'fuelConsumptionKWhAt100Percent'], unit: 'Nm3/kvahour' },
    { field: (generatorNumber: number) => ['scenario', 'eco', `fuelPriceType${generatorNumber}`], unit: '$/kwh' }
  ]
};

export const buildGeneratorModule = ({ references, ruleHelper }: Dependencies) => {
  @Module({
    namespaced: true
  })
  class GeneratorModule extends VuexModule {

    private presets: Preset<typeof GeneratorTypeRef> | null = null;
    private presetsLoading: Promise<[Preset<typeof GeneratorTypeRef>, Preset<typeof GeneratorTechnology>]> | null = null;

    @Action({ rawError: true })
    public async loadPresets() {
      if (this.presetsLoading !== null) {
        return this.presetsLoading;
      }
      const referencesPromise = references.getGeneratorRefs().then(generatorRefs => {
        this.context.commit('setPresets', generatorRefs);
        return generatorRefs;
      });
      this.context.commit('setPresetsLoading', Promise.all([referencesPromise, references.getFuelRefs()]));
      return this.presetsLoading;
    }

    @Action({ rawError: true })
    public updateTechnology({ technology, generatorNumber }: UpdateTechnologyPayload): void {
      unitsReferential[technology].forEach(({ field, unit }) => {
        const rule = ruleHelper.getFromRulesDictionary(field(generatorNumber)) as MinMaxRule;
        rule.unitString = unit;
      });
    }

    @Mutation
    private setPresets(payload: Preset<typeof GeneratorTypeRef> | null) {
      this.presets = payload;
    }

    @Mutation
    private setPresetsLoading(payload: Promise<[Preset<typeof GeneratorTypeRef>, Preset<typeof GeneratorTechnology>]>) {
      this.presetsLoading = payload;
    }

    get getConfig(): ScenarioExt {
      return new ScenarioExt(this.context.rootGetters['scenario/getFields']);
    }

    get getFields(): ScenarioGeneratorExt {
      return this.getConfig.generator ? new ScenarioGeneratorExt(this.getConfig.generator) : getDefaultFields();
    }

    get getPresets(): Preset<typeof GeneratorTypeRef> | null {
      return this.presets;
    }
  }

  return GeneratorModule;
};
